import { Check, CheckCheck, Clock, MessageSquareX, Send } from 'lucide-react';
import type { SMSStatistic } from 'types/sms.type';
import {
    formatCount,
    formatStatusPercent,
    type CallStatusSummarySubStatus,
    type StatusIconTheme,
} from '../../CallAnalytics/CallStatusSummary/callStatusSummary.utils';

export type SMSStatusSummaryCardVariant = 'queued' | 'sending' | 'sent' | 'delivered' | 'failed';

export type SMSStatusSummarySubStatus = CallStatusSummarySubStatus;

export type SMSStatusSummaryCard = {
    id: SMSStatusSummaryCardVariant;
    label: string;
    count: number;
    percent: number;
    variant: SMSStatusSummaryCardVariant;
    tooltip: string;
    breakdownTitle: string;
    breakdownSubtitle: string;
    subStatuses: SMSStatusSummarySubStatus[];
};

export const SMS_STATUS_ICON_THEME: Record<SMSStatusSummaryCardVariant, StatusIconTheme> = {
    queued: {
        icon: Clock,
        color: 'var(--slai-ui-primary-purple-dark)',
        softBackground: 'var(--slai-ui-primary-purple-light)',
    },
    sending: {
        icon: Send,
        color: 'var(--slai-ui-primary-purple-dark)',
        softBackground: 'var(--slai-ui-primary-purple-light)',
    },
    sent: {
        icon: Check,
        color: 'var(--slai-ui-primary-purple-dark)',
        softBackground: 'var(--slai-ui-primary-purple-light)',
    },
    delivered: {
        icon: CheckCheck,
        color: 'var(--slai-ui-primary-purple-dark)',
        softBackground: 'var(--slai-ui-primary-purple-light)',
    },
    failed: {
        icon: MessageSquareX,
        color: 'var(--slai-ui-primary-purple-dark)',
        softBackground: 'var(--slai-ui-primary-purple-light)',
    },
};

const asCount = (value: number | null | undefined): number => value ?? 0;

const normalizeSMSStatistic = (smsStats?: SMSStatistic): SMSStatistic => ({
    pending: asCount(smsStats?.pending),
    queued: asCount(smsStats?.queued),
    overDue: asCount(smsStats?.overDue),
    sending: asCount(smsStats?.sending),
    retrying: asCount(smsStats?.retrying),
    sent: asCount(smsStats?.sent),
    delivered: asCount(smsStats?.delivered),
    failed: asCount(smsStats?.failed),
    total: asCount(smsStats?.total),
});

/** Messages already handed to the carrier — excludes queued, pending and overdue. */
export const getSentSMSTotal = (smsStats?: SMSStatistic): number => {
    const stats = normalizeSMSStatistic(smsStats);
    return Math.max(0, stats.total - stats.pending - stats.queued - stats.overDue);
};

const buildSubStatuses = (
    entries: Array<{ id: string; label: string; count: number }>,
    categoryTotal: number
): SMSStatusSummarySubStatus[] =>
    entries.map((entry) => ({
        id: entry.id,
        label: entry.label,
        count: entry.count,
        percent: formatStatusPercent(entry.count, categoryTotal),
    }));

export const buildSMSStatusSummaryCards = (smsStats?: SMSStatistic): SMSStatusSummaryCard[] => {
    const stats = normalizeSMSStatistic(smsStats);
    // Delivery rates are a share of the messages that already left the queue.
    const sentTotal = getSentSMSTotal(stats);
    const percent = (count: number) => formatStatusPercent(count, sentTotal);

    const queuedTotal = stats.queued + stats.pending + stats.overDue;
    const sendingTotal = stats.sending + stats.retrying;
    const acceptedTotal = stats.sent + stats.delivered;

    return [
        {
            id: 'queued',
            label: 'Queued / Overdue',
            count: queuedTotal,
            // Not sent yet, so a rate against sent messages is not meaningful.
            percent: 0,
            variant: 'queued',
            tooltip:
                'Queued — messages scheduled for today. Overdue — messages scheduled for prior days and still not sent.',
            breakdownTitle: 'Queued / Overdue breakdown',
            breakdownSubtitle: `Split of ${formatCount(queuedTotal)} messages waiting to be sent`,
            subStatuses: buildSubStatuses(
                [
                    { id: 'queued', label: 'Queued', count: stats.queued },
                    { id: 'pending', label: 'Pending', count: stats.pending },
                    { id: 'overdue', label: 'Overdue', count: stats.overDue },
                ],
                queuedTotal
            ),
        },
        {
            id: 'sending',
            label: 'Sending',
            count: sendingTotal,
            percent: percent(sendingTotal),
            variant: 'sending',
            tooltip: 'Messages being handed to the carrier. Retrying — a send attempt failed and will be repeated.',
            breakdownTitle: 'Sending breakdown',
            breakdownSubtitle: `Split of ${formatCount(sendingTotal)} messages in flight`,
            subStatuses: buildSubStatuses(
                [
                    { id: 'sending', label: 'Sending', count: stats.sending },
                    { id: 'retrying', label: 'Retrying', count: stats.retrying },
                ],
                sendingTotal
            ),
        },
        {
            id: 'sent',
            label: 'Sent',
            count: stats.sent,
            percent: percent(stats.sent),
            variant: 'sent',
            tooltip: 'Messages accepted by the carrier while the delivery receipt is still pending.',
            breakdownTitle: 'Sent breakdown',
            breakdownSubtitle: `Delivery receipts for ${formatCount(acceptedTotal)} accepted messages`,
            subStatuses: buildSubStatuses(
                [
                    { id: 'awaitingReceipt', label: 'Awaiting receipt', count: stats.sent },
                    { id: 'delivered', label: 'Delivered', count: stats.delivered },
                ],
                acceptedTotal
            ),
        },
        {
            id: 'delivered',
            label: 'Delivered',
            count: stats.delivered,
            percent: percent(stats.delivered),
            variant: 'delivered',
            tooltip: 'Messages confirmed as delivered to the contact handset.',
            breakdownTitle: 'Delivered breakdown',
            breakdownSubtitle: `Delivered share of ${formatCount(sentTotal)} sent messages`,
            subStatuses: buildSubStatuses(
                [
                    { id: 'delivered', label: 'Delivered', count: stats.delivered },
                    {
                        id: 'notDelivered',
                        label: 'Not delivered',
                        count: Math.max(0, sentTotal - stats.delivered),
                    },
                ],
                sentTotal
            ),
        },
        {
            id: 'failed',
            label: 'Failed',
            count: stats.failed,
            percent: percent(stats.failed),
            variant: 'failed',
            tooltip:
                'Messages the carrier could not deliver — invalid or landline numbers, blocked contacts and carrier errors.',
            breakdownTitle: 'Failed breakdown',
            breakdownSubtitle: `Failure share of ${formatCount(sentTotal)} sent messages`,
            subStatuses: buildSubStatuses(
                [
                    { id: 'failed', label: 'Failed', count: stats.failed },
                    { id: 'succeeded', label: 'No failure', count: Math.max(0, sentTotal - stats.failed) },
                ],
                sentTotal
            ),
        },
    ];
};
